import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { Adapt } from './lib/adapt.js';
import { SceneClock } from './lib/scene-clock.js';

export const PROFILE = {
  id: 'murmuration',
  family: 'organic',
  danceability: 0.48,
  requiresTier: 0,
  energyLevel: 0.30,
  bpmMin: 60,
  bpmMax: 140,
  movement: 0.40,
  tonality: 0.85,
  enabled: true
};

// MURMURATION : au crépuscule, une nuée d'étourneaux danse au-dessus d'un
// marais. Preset lounge (R9) : aucun flash ; les accents lancent des VAGUES.
//
// LA NUÉE EST UNE NAPPE (v2). Une murmuration se lit comme une nappe ou un
// ruban d'oiseaux qui se plie dans l'espace : vue par la tranche, une ligne
// noire et dense ; vue de face, claire, presque transparente. Chaque oiseau
// a une place (u, v) FIXE sur une surface paramétrique 2,5 fois plus large
// que haute, et un petit décalage w dans son épaisseur (3,5 % de la largeur).
// Le vertex shader déforme la nappe à partir de quelques uniforms :
//   - une onde de flexion en S (le long de u), une seconde le long de v ;
//   - deux courbures (u et v : la nappe se roule, se replie en deux) ;
//   - l'enroulement d'un bord (une courbure qui croît vers u = 1) ;
//   - une torsion autour de son grand axe ;
//   - un bulbe qui gonfle (et épaissit la nappe), une taille de guêpe
//     (sablier), une traînée (la moitié u > 0 s'amincit et s'allonge) ;
// puis l'oriente en 3D et la projette. Le contraste vient tout seul : là où
// la nappe se replie ou se présente par la tranche, les oiseaux se
// superposent et la zone noircit ; ailleurs on voit le ciel entre eux.
// Aucune position n'est calculée côté CPU : il n'écrit que des scalaires et
// une matrice de rotation par frame.
//
// LA VAGUE : une onde de VIRAGE parcourt la nappe en 1,9 s : dans sa bande,
// les oiseaux s'inclinent (leur tiret s'allonge et fonce), se resserrent, et
// la nappe se creuse un peu - une ondulation sombre traverse la nuée.
//
// LES OISEAUX : des tirets noirs (luminance 0,02) de 2 à 3 px qui
// s'allongent et se raccourcissent avec le battement d'ailes (phase propre),
// plus longs dans les vagues ; ceux de l'arrière de la nappe un peu plus
// petits et voilés (10 % au plus).
//
// LA CHORÉGRAPHIE : des formes types (ruban qui s'enroule, nappe repliée en
// deux, sablier, bulbe qui s'étire en traînée, S, nappe par la tranche) ;
// une nouvelle cible toutes les 6 à 10 s (plus vite quand adapt.intensity
// monte), atteinte en 4 à 6 s. La nuée occupe 40 à 55 % de la largeur, dans
// les deux tiers supérieurs, et peut passer derrière la pochette (le masque
// safeZone l'y atténue). Cycle de scène (SceneClock) : départ au hasard, la
// nuée danse dès la première image ; plongée vers les roseaux seulement si
// director.sceneRemaining l'annonce.
//
// LE DÉCOR : un quad de ciel (dégradé, lueur du soleil couché, nuages
// filandreux éclairés par-dessous, rangée d'arbres lointaine pâle et
// bleutée), les oiseaux, puis le premier plan en CHAMPS DE DISTANCE (bords
// anticrénelés, plus aucun escalier) : des arbres au tronc qui se divise,
// couronnes de 5 à 12 touffes bruitées percées de trous ; des roseaux de
// 1 à 2 px, courbés, avec massettes et feuilles en lame, qui se balancent
// lentement au vent. Silhouettes noires (R13), un liseré seulement sur les
// tiges devant le soleil.

const COUNTS = { 0: 10000, 1: 18000, 2: 30000 };
const HORIZON = -0.30;           // la ligne d'horizon (unités de hauteur, centre = 0, hauteur = 1)
const SUN_X = 0.42;
const SHAPE_S = [6, 10];         // une nouvelle forme toutes les 6 (intense) à 10 s (calme)
const WAVE_S = 1.9;              // une vague traverse la nappe en 1,9 s
const WAVE_GAP_S = 2.5;          // au plus une vague toutes les 2,5 s (lounge : la masse respire entre deux)
const WAVE_FALLBACK_S = 12;      // sans accent, une vague naturelle au plus tard
const SPLIT_S = 9;
const SPLIT_COOLDOWN_S = 30;
const DIVE_S = 3;
const SCENE_S = 20;
const HALF_W = 0.42;             // demi-largeur de la nappe à plat (unités de hauteur)

function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}
function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function smoother(x) { const t = clamp01(x); return t * t * t * (t * (t * 6 - 15) + 10); }

// les formes types de la nappe :
//   s1 (amplitude, fréquence) : flexion en S le long de u ; s2 : le long de v
//   ku, kv : courbures ; roll : enroulement du bord u = 1 ; twist : torsion
//   bulb : bulbe (en u = -0,4) ; waist : taille de guêpe ; trail : traînée
//   aspect : hauteur / largeur ; rot : orientation (euler)
const ARCHETYPES = [
  { name: 'ruban qui s\'enroule', s1: [0.10, 1.3], s2: [0.02, 1.0], ku: 0.6, kv: 0.0, roll: 3.2, twist: 1.1, bulb: 0.0, waist: 0.0, trail: 0.1, aspect: 0.28, rot: [0.5, 0.3, 0.1] },
  { name: 'nappe repliée en deux', s1: [0.04, 1.0], s2: [0.0, 1.0], ku: 0.0, kv: 4.2, roll: 0.0, twist: 0.3, bulb: 0.0, waist: 0.1, trail: 0.0, aspect: 0.50, rot: [0.9, 0.2, 0.2] },
  { name: 'sablier', s1: [0.06, 1.2], s2: [0.03, 1.5], ku: 0.4, kv: 0.0, roll: 0.0, twist: 1.6, bulb: 0.0, waist: 0.75, trail: 0.0, aspect: 0.48, rot: [0.35, 0.6, -0.2] },
  { name: 'bulbe en traînée', s1: [0.05, 0.8], s2: [0.02, 1.0], ku: 0.8, kv: 0.0, roll: 0.5, twist: 0.4, bulb: 0.22, waist: 0.0, trail: 0.75, aspect: 0.42, rot: [0.6, -0.4, 0.15] },
  { name: 'S', s1: [0.16, 1.6], s2: [0.04, 1.2], ku: 0.0, kv: 0.6, roll: 0.0, twist: 0.5, bulb: 0.05, waist: 0.15, trail: 0.0, aspect: 0.40, rot: [1.0, 0.1, 0.05] },
  { name: 'nappe par la tranche', s1: [0.07, 2.0], s2: [0.03, 1.0], ku: 0.5, kv: 0.8, roll: 0.0, twist: 0.6, bulb: 0.08, waist: 0.0, trail: 0.2, aspect: 0.45, rot: [1.35, 0.25, 0.0] }
];

const quadVertex = `
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;

const NOISE = `
  float hash(vec2 p) { vec3 q = fract(vec3(p.xyx) * 0.1031); q += dot(q, q.yzx + 33.33); return fract((q.x + q.y) * q.z); }
  float vnoise(vec2 p) { vec2 i = floor(p), f = fract(p); f = f * f * (3.0 - 2.0 * f);
    return mix(mix(hash(i), hash(i + vec2(1.0, 0.0)), f.x), mix(hash(i + vec2(0.0, 1.0)), hash(i + 1.0), f.x), f.y); }
  float fbm(vec2 p) { float v = 0.0, a = 0.5; for (int i = 0; i < 5; i++) { v += a * vnoise(p); p = p * 2.03 + 17.1; a *= 0.5; } return v; }
  vec3 toLin(vec3 c) { return pow(c, vec3(2.2)); }
`;

const skyFragment = `
  precision highp float;
  uniform float uAspect;
  uniform vec2 uStageCenter;
  uniform float uTime;
  varying vec2 vUv;
  ${NOISE}
  void main() {
    vec2 p = 0.5 * ((vUv * 2.0 - 1.0) * vec2(uAspect, 1.0) - uStageCenter);
    float h = clamp((p.y - ${HORIZON.toFixed(3)}) / (0.5 - ${HORIZON.toFixed(3)}), 0.0, 1.0);
    vec3 c0 = vec3(0.96, 0.56, 0.24), c1 = vec3(0.86, 0.46, 0.46), c2 = vec3(0.42, 0.32, 0.52), c3 = vec3(0.10, 0.14, 0.30);
    vec3 col = h < 0.18 ? mix(c0, c1, h / 0.18) : h < 0.48 ? mix(c1, c2, (h - 0.18) / 0.30) : mix(c2, c3, (h - 0.48) / 0.52);
    vec2 sd = (p - vec2(${SUN_X.toFixed(2)}, ${HORIZON.toFixed(3)} - 0.02)) * vec2(0.55, 1.0);
    float glow = exp(-dot(sd, sd) / 0.020) * 0.55 + exp(-dot(sd, sd) / 0.15) * 0.22;
    col += vec3(1.0, 0.72, 0.38) * glow;
    vec2 q = vec2(p.x * 1.6 + uTime * 0.004, p.y * 9.0);
    float n = fbm(q + vec2(fbm(q * 0.7 + 3.0) * 1.5, 0.0));
    float band = smoothstep(0.05, 0.22, p.y + 0.12) * (1.0 - smoothstep(0.30, 0.45, p.y));
    float cloud = smoothstep(0.52, 0.80, n) * band;
    float under = smoothstep(0.80, 0.52, fbm(q + vec2(0.0, 0.35)));
    vec3 lit = mix(vec3(0.55, 0.30, 0.42), vec3(1.0, 0.62, 0.44), under * (1.0 - h * 0.6));
    col = mix(col, lit, cloud * 0.65);
    // la rangée d'arbres lointaine : un liseré de cimes bosselées sur l'horizon,
    // plus pâle et bleuté (la profondeur), sous le marais du premier plan
    float ridge = ${HORIZON.toFixed(3)} + 0.008 + 0.012 * fbm(vec2(p.x * 7.0, 7.0)) + 0.011 * vnoise(vec2(p.x * 130.0, 3.0)) + 0.005 * vnoise(vec2(p.x * 330.0, 5.0));
    float far = 1.0 - smoothstep(ridge - 0.0015, ridge + 0.0015, p.y);
    col = mix(col, mix(col, vec3(0.30, 0.27, 0.42), 0.55), far);
    col += (hash(gl_FragCoord.xy) - 0.5) / 255.0;
    // R10 (lounge) : luminance moyenne du cadre sous 0,35
    gl_FragColor = vec4(toLin(col * 0.92), 1.0);
  }
`;

const birdVertex = `
  precision highp float;
  attribute vec3 aRef;           // u, v (-1..1, contour organique), w (épaisseur, -1..1)
  attribute vec3 aRnd;           // phase des remous, phase du battement, aléa
  uniform float uAspect;
  uniform vec2 uStageCenter;
  uniform float uTime;
  uniform vec2 uCenter;
  uniform float uScale;
  uniform mat3 uRot;
  uniform vec4 uS;               // flexion u : amplitude, fréquence ; flexion v : amplitude, fréquence
  uniform vec4 uK;               // courbure u, courbure v, enroulement, torsion
  uniform vec4 uB;               // bulbe, taille, traînée, rapport hauteur/largeur
  uniform vec4 uWave[2];         // direction (u, v), front, amplitude
  uniform vec4 uSplit;           // direction (x, y), écart, part
  uniform vec3 uDive;            // part, cible x, cible y
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;
  varying float vLen;            // demi-longueur du tiret (px)
  varying float vAng;            // son inclinaison
  varying float vHaze;           // voile atmosphérique (0..0,1)
  varying float vFade;           // atténuation derrière la pochette
  vec2 bendXZ(float x, float z, float k) {
    // une ligne x, décalée de z selon sa normale, enroulée sur un arc de courbure k
    if (abs(k) < 1e-3) return vec2(x, z);
    float a = x * k;
    return vec2(sin(a) / k - z * sin(a), (1.0 - cos(a)) / k + z * cos(a));
  }
  void main() {
    float u = aRef.x, v = aRef.y, w = aRef.z, t = uTime;
    // LA VAGUE DE VIRAGE : une bande qui parcourt la nappe ; les oiseaux s'y resserrent
    float bank = 0.0, dip = 0.0;
    for (int k = 0; k < 2; k++) {
      vec4 wv = uWave[k];
      if (wv.w <= 0.0) continue;
      float s = dot(vec2(u, v), wv.xy) - wv.z;
      float g = exp(-s * s / 0.03);
      vec2 uv2 = vec2(u, v) - wv.xy * s * g * 0.45 * wv.w;
      u = uv2.x; v = uv2.y;
      bank = max(bank, g * wv.w);
      dip += g * wv.w;
    }
    // la nappe à plat : largeur 2, hauteur aspect x 2 ; sablier et traînée
    float aspect = uB.w;
    float waist = 1.0 - uB.y * exp(-u * u / 0.06);
    float trail = 1.0 - uB.z * 0.75 * smoothstep(0.0, 1.0, u);
    float x = u * (1.0 + 0.45 * uB.z * smoothstep(0.0, 1.0, u));      // la traînée s'allonge
    float y = v * aspect * waist * trail;
    // flexions en S, remous internes lents, bulbe
    float z = uS.x * sin(3.14159 * uS.y * u + t * 0.23) + uS.z * sin(3.14159 * uS.w * v - t * 0.19);
    z += 0.025 * sin(u * 5.0 + v * 3.0 + t * 0.6 + aRnd.x) + 0.015 * sin(v * 7.0 - t * 0.45);
    float bulb = uB.x * exp(-((u + 0.4) * (u + 0.4) + v * v * 2.0) / 0.10);
    z += bulb;
    // l'épaisseur : 5 % de la largeur, plus épaisse dans le bulbe ; la vague creuse
    z += w * (0.035 + 0.6 * bulb) - 0.035 * dip;
    // courbure le long de u (avec l'enroulement du bord u = 1), puis le long de v
    float ku = uK.x + uK.z * smoothstep(0.25, 1.0, u);
    vec2 xz = bendXZ(x, z, ku);
    vec2 yz = bendXZ(y, xz.y, uK.y);
    vec3 P = vec3(xz.x, yz.x, yz.y);
    // la torsion autour du grand axe
    float tw = uK.w * u;
    P.yz = vec2(cos(tw) * P.y - sin(tw) * P.z, sin(tw) * P.y + cos(tw) * P.z);
    vec3 W = uRot * P;
    vec2 s2 = W.xy;
    // la division : deux moitiés qui s'écartent
    float side = sign(dot(aRef.xy, uSplit.xy) + 1e-4);
    s2 += uSplit.xy * side * uSplit.z * uSplit.w;
    vec2 pos = uCenter + s2 * uScale;
    // la plongée : les plus bas d'abord, vers les roseaux
    if (uDive.x > 0.0) {
      float delay = 0.55 * (aRef.y * 0.5 + 0.5) + 0.15 * aRnd.z;
      float kd = smoothstep(0.0, 1.0, clamp((uDive.x - delay) / 0.30, 0.0, 1.0));
      vec2 tgt = uDive.yz + vec2(aRef.x * 0.05, aRef.z * 0.01);
      vec2 ctrl = vec2(mix(pos.x, tgt.x, 0.3), pos.y + 0.04);
      pos = mix(mix(pos, ctrl, kd), mix(ctrl, tgt, kd), kd);
    }
    // le battement d'ailes : le tiret s'allonge et se raccourcit (2 à 3 px), plus long en virage
    float fl = 0.5 + 0.5 * sin(t * (11.0 + 5.0 * aRnd.z) + aRnd.y * 6.283);
    float depth = clamp(0.5 + 0.5 * W.z / 0.35, 0.0, 1.0);          // 1 devant, 0 derrière
    float size = mix(0.80, 1.0, depth);
    vLen = (0.55 + 0.45 * fl) * (1.0 + 0.9 * bank) * size;
    vAng = (aRnd.z - 0.5) * 0.5 + bank * 0.6 * (aRnd.y < 0.5 ? -1.0 : 1.0);
    vHaze = 0.10 * (1.0 - depth);
    // derrière la pochette (masque safeZone), la nuée passe atténuée
    vec2 sc = pos * 2.0 + uStageCenter;
    vec2 qq = abs(sc - uSafeCenter) - uSafeHalfSize;
    float dBox = length(max(qq, 0.0)) + min(max(qq.x, qq.y), 0.0);
    vFade = mix(0.45, 1.0, smoothstep(0.0, uSafeFeather, dBox));
    gl_Position = vec4(sc.x / uAspect, sc.y, 0.0, 1.0);
    gl_PointSize = 7.0;
  }
`;

const birdFragment = `
  precision highp float;
  varying float vLen;
  varying float vAng;
  varying float vHaze;
  varying float vFade;
  void main() {
    // un tiret : un segment de demi-longueur vLen px, ~1 px d'épaisseur, incliné de vAng
    vec2 d = (gl_PointCoord - 0.5) * 7.0;
    float c = cos(vAng), s = sin(vAng);
    vec2 q = vec2(c * d.x + s * d.y, -s * d.x + c * d.y);
    vec2 r = vec2(max(abs(q.x) - vLen, 0.0), q.y);
    float cov = 1.0 - smoothstep(0.30, 0.85, length(r));
    if (cov <= 0.0) discard;
    // noir (luminance 0,02), voilé de 10 % au plus vers l'arrière de la nappe
    vec3 ink = mix(vec3(0.0016), vec3(0.20, 0.13, 0.20), vHaze);
    gl_FragColor = vec4(ink, cov * 0.92 * vFade);
  }
`;

const landFragment = `
  precision highp float;
  uniform float uAspect;
  uniform vec2 uStageCenter;
  uniform float uPx;
  uniform float uTime;
  varying vec2 vUv;
  ${NOISE}
  float sdSeg(vec2 p, vec2 a, vec2 b, float ra, float rb) {
    vec2 pa = p - a, ba = b - a; float h = clamp(dot(pa, ba) / dot(ba, ba), 0.0, 1.0);
    return length(pa - ba * h) - mix(ra, rb, h);
  }
  // le premier plan en distance signée (unités de hauteur, négatif dedans) ;
  // reed = 1 si la forme la plus proche est un roseau (pour le liseré)
  float scene(vec2 p, out float reed) {
    float t = uTime;
    reed = 0.0;
    float x = p.x;
    float ground = ${HORIZON.toFixed(3)} - 0.035 + 0.010 * sin(x * 3.1) + 0.006 * (vnoise(vec2(x * 7.0, 1.0)) - 0.5);
    float d = p.y - ground;
    // LES ROSEAUX : deux rangs décalés ; une tige (1,2 px), parfois une
    // massette, parfois une feuille en lame ; courbés, balancés lentement
    if (p.y < ground + 0.13) {
      for (int layer = 0; layer < 2; layer++) {
        float fl = float(layer);
        float cw = 0.0058;
        float xo = x + fl * cw * 0.5;
        float ci = floor(xo / cw);
        for (int k = -1; k <= 1; k++) {
          float cid = ci + float(k);
          float zone = smoothstep(0.30, 0.60, vnoise(vec2(cid * 0.045 + fl * 3.0, 2.0)));
          if (zone <= 0.02) continue;
          float hr = hash(vec2(cid, 4.0 + fl));
          float tall = (0.025 + 0.085 * hr * hr) * zone * (1.0 - 0.35 * fl);
          float bx = (cid + 0.5) * cw - fl * cw * 0.5 + (hash(vec2(cid, 7.0)) - 0.5) * cw * 0.6;
          float curve = (hash(vec2(cid, 9.0 + fl)) - 0.5) * 0.020;
          float wind = 0.0035 * sin(t * 0.55 + cid * 0.07) + 0.0015 * sin(t * 1.1 + cid * 0.31);
          float tt = clamp((p.y - ground) / max(tall, 1e-4), 0.0, 1.0);
          float cx = bx + (curve + wind) * tt * tt;
          float kind = hash(vec2(cid, 11.0 + fl));
          float dr;
          if (kind < 0.30) {
            // une feuille en lame : large à la base, effilée, plus courbée
            float lw = mix(0.0018, 0.0, tt);
            float lx = bx + (curve * 2.2 + wind) * tt * tt;
            dr = max(abs(x - lx) - lw, max(ground - p.y, p.y - (ground + tall)));
          } else {
            // une tige de 1,2 px (constante en pixels), jusqu'au sommet
            dr = max(abs(x - cx) - 0.6 * uPx, max(ground - 0.01 - p.y, p.y - (ground + tall)));
            if (kind > 0.62) {
              // la massette : un épi ovale juste sous le sommet
              vec2 hc = vec2(bx + (curve + wind) * 0.74, ground + tall * 0.86);
              vec2 hq = (p - hc) / vec2(0.0020, 0.0085);
              dr = min(dr, (length(hq) - 1.0) * 0.0020);
            }
          }
          if (dr < d) { d = dr; reed = 1.0; }
        }
      }
    }
    // LES ARBRES : un tronc qui se divise en branches, une couronne de 6 à 11
    // touffes bruitées (union de cercles), percée de trous où le ciel passe
    for (int i = 0; i < 4; i++) {
      float fi = float(i);
      float tx = i == 0 ? -0.66 : (i == 1 ? -0.10 : (i == 2 ? 0.66 : 1.02));
      float th = i == 0 ? 0.19 : (i == 1 ? 0.12 : (i == 2 ? 0.16 : 0.22));
      float base = ${HORIZON.toFixed(3)} - 0.03;
      if (abs(x - tx) > 0.17 || p.y > base + th + 0.11) continue;
      float sway = 0.0025 * sin(t * 0.35 + fi * 1.7);
      vec2 fork = vec2(tx + 0.004 * (hash(vec2(fi, 1.0)) - 0.5), base + th * 0.55);
      float dt = sdSeg(p, vec2(tx, base - 0.01), fork, 0.0055, 0.0035);
      float crown = 1e3;
      int nT = 8 + int(hash(vec2(fi, 2.0)) * 5.0);                     // 8 à 12 touffes
      for (int j = 0; j < 12; j++) {
        if (j >= nT) break;
        float fj = float(j);
        // des touffes serrées qui se chevauchent : une couronne, pas des boules séparées
        float a = 3.14159 * (0.08 + 0.84 * hash(vec2(fi, fj + 3.0)));
        float rr = th * (0.18 + 0.30 * hash(vec2(fi, fj + 13.0)));
        vec2 c = fork + vec2(0.0, th * 0.18) + vec2(cos(a) * rr * 1.35, sin(a) * rr * 0.9) + vec2(sway * (0.5 + fj * 0.1), 0.0);
        float rad = th * (0.15 + 0.10 * hash(vec2(fi, fj + 23.0)));
        float edge = rad * (0.82 + 0.36 * vnoise((p - c) / rad * 2.2 + fi * 5.0 + fj));
        crown = min(crown, length(p - c) - edge);
        if (j < 4) dt = min(dt, sdSeg(p, fork, mix(fork, c, 0.7), 0.0030, 0.0014));
      }
      // les trous : du ciel à travers le feuillage (seulement à l'intérieur de la couronne)
      if (crown < -0.007) {
        float hole = fbm((p - fork) * 55.0 + fi * 9.0) - 0.74;
        crown = max(crown, hole * 0.02);
      }
      float dtree = min(dt, crown);
      if (dtree < d) { d = dtree; reed = 0.0; }
    }
    return d;
  }
  void main() {
    vec2 p = 0.5 * ((vUv * 2.0 - 1.0) * vec2(uAspect, 1.0) - uStageCenter);
    if (p.y > ${HORIZON.toFixed(3)} + 0.37) discard;
    float reed;
    float d = scene(p, reed);
    // anticrénelage par la distance : une transition d'un pixel
    float cov = clamp(0.5 - d / uPx, 0.0, 1.0);
    if (cov <= 0.0) discard;
    // R13 : corps noir ; un léger liseré seulement sur les tiges devant le
    // soleil couchant, du côté qui le regarde
    vec2 toSun = normalize(vec2(${SUN_X.toFixed(2)}, ${HORIZON.toFixed(3)} + 0.05) - p);
    float near = exp(-pow((p.x - ${SUN_X.toFixed(2)}) / 0.16, 2.0)) * reed;
    float rim = 0.0;
    if (near > 0.01) {
      float r2;
      float dOut = scene(p + toSun * 2.0 * uPx, r2);
      rim = near * clamp(dOut / uPx, 0.0, 1.0);
    }
    vec3 col = vec3(0.004) + rim * pow(vec3(1.0, 0.64, 0.32), vec3(2.2)) * 0.9;
    gl_FragColor = vec4(col, cov);
  }
`;

export class MurmurationVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = o.qualityTier != null ? o.qualityTier : 1;
    this._count = COUNTS[this._tier] || COUNTS[1];
    this._dbgWave = urlParam('murmwave') === '1';
    this._dbgSplit = urlParam('murmsplit') === '1';
    this._dbgDive = urlParam('murmdive') === '1';
  }

  // lounge : ni flash ni séparation RGB ; un bloom très léger pour la lueur du soleil
  get postProcessing() { return { bloomStrength: 0.08, bloomThreshold: 0.85, aberration: 0, grain: 0.012, vignette: 0.35, toneMapping: THREE.NoToneMapping, toneMappingExposure: 1 }; }

  init(scene) {
    this._scene = scene;
    this._skyU = { uAspect: safeZoneUniforms.uAspect, uStageCenter: safeZoneUniforms.uStageCenter, uTime: { value: 0 } };
    this._sky = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({ uniforms: this._skyU, vertexShader: quadVertex, fragmentShader: skyFragment, depthTest: false, depthWrite: false }));
    this._sky.frustumCulled = false; this._sky.renderOrder = -1000;

    // les oiseaux : une place (u, v) fixe sur la nappe, au contour organique
    // (superellipse bruitée), et un décalage w dans son épaisseur
    const n = this._count, ref = new Float32Array(n * 3), rnd = new Float32Array(n * 3);
    let seed = 987654321;
    const rand = () => { seed = (seed * 1664525 + 1013904223) >>> 0; return seed / 4294967296; };
    for (let i = 0; i < n; i++) {
      let u, v;
      for (;;) {
        u = rand() * 2 - 1; v = rand() * 2 - 1;
        const ang = Math.atan2(v, u);
        const edge = 1 - 0.18 * (0.5 + 0.5 * Math.sin(ang * 3 + 1.3)) - 0.10 * (0.5 + 0.5 * Math.sin(ang * 7 + 0.4));
        if (Math.pow(Math.abs(u), 2.4) + Math.pow(Math.abs(v), 2.4) < edge) break;
      }
      ref[i * 3] = u; ref[i * 3 + 1] = v; ref[i * 3 + 2] = rand() + rand() - 1;
      rnd[i * 3] = rand() * 6.283; rnd[i * 3 + 1] = rand(); rnd[i * 3 + 2] = rand();
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(n * 3), 3));
    geo.setAttribute('aRef', new THREE.BufferAttribute(ref, 3));
    geo.setAttribute('aRnd', new THREE.BufferAttribute(rnd, 3));
    this._birdU = {
      uAspect: safeZoneUniforms.uAspect, uStageCenter: safeZoneUniforms.uStageCenter, uTime: { value: 0 },
      uCenter: { value: new THREE.Vector2() }, uScale: { value: HALF_W }, uRot: { value: new THREE.Matrix3() },
      uS: { value: new THREE.Vector4() }, uK: { value: new THREE.Vector4() }, uB: { value: new THREE.Vector4(0, 0, 0, 0.4) },
      uWave: { value: [new THREE.Vector4(1, 0, 0, 0), new THREE.Vector4(1, 0, 0, 0)] },
      uSplit: { value: new THREE.Vector4(1, 0, 0.3, 0) }, uDive: { value: new THREE.Vector3(0, 0.1, HORIZON - 0.04) },
      uSafeCenter: safeZoneUniforms.uSafeCenter, uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather
    };
    this._birds = new THREE.Points(geo, new THREE.ShaderMaterial({ uniforms: this._birdU, vertexShader: birdVertex, fragmentShader: birdFragment, transparent: true, depthTest: false, depthWrite: false }));
    this._birds.frustumCulled = false; this._birds.renderOrder = -999;

    this._landU = { uAspect: safeZoneUniforms.uAspect, uStageCenter: safeZoneUniforms.uStageCenter, uPx: { value: 1 / 720 }, uTime: { value: 0 } };
    this._land = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({ uniforms: this._landU, vertexShader: quadVertex, fragmentShader: landFragment, transparent: true, depthTest: false, depthWrite: false }));
    this._land.frustumCulled = false; this._land.renderOrder = -998;
    const self = this;
    this._sky.onBeforeRender = function (renderer) { self._beforeRender(renderer); };
    scene.add(this._sky); scene.add(this._birds); scene.add(this._land);

    this._adapt = new Adapt();
    this._clock = new SceneClock({ period: SCENE_S, start: 'random' });
    this._t = this._clock.elapsed + 37;
    this._seed = (Math.random() * 1e9) >>> 0;
    this._from = this._makeShape(); this._to = this._makeShape(this._from.idx);
    this._shapeT0 = this._t - 2.5; this._shapeDur = 5; this._nextShapeAt = this._t + 3;
    this._waves = []; this._lastWave = this._t - 6; this._split = null; this._lastSplit = this._t - SPLIT_COOLDOWN_S + 12;
    this._metamorphoses = 0; this._waveCount = 0; this._splitCount = 0; this._dive = 0;
    this._rot = new THREE.Matrix4(); this._eu = new THREE.Euler();
  }

  _rand() { this._seed = (this._seed * 1664525 + 1013904223) >>> 0; return this._seed / 4294967296; }

  _makeShape(notIdx) {
    let idx = Math.floor(this._rand() * ARCHETYPES.length);
    if (idx === notIdx) idx = (idx + 1 + Math.floor(this._rand() * (ARCHETYPES.length - 1))) % ARCHETYPES.length;
    const a = ARCHETYPES[idx], j = () => 0.85 + 0.3 * this._rand(), sgn = () => (this._rand() < 0.5 ? -1 : 1);
    return {
      idx, name: a.name,
      s1: [a.s1[0] * j() * sgn(), a.s1[1] * j()], s2: [a.s2[0] * j(), a.s2[1] * j()],
      ku: a.ku * j() * sgn(), kv: a.kv * j() * sgn(), roll: a.roll * j() * sgn(), twist: a.twist * j() * sgn(),
      bulb: a.bulb * j(), waist: a.waist * j(), trail: a.trail * j(), aspect: a.aspect * j(),
      rot: [a.rot[0] + (this._rand() - 0.5) * 0.4, a.rot[1] + (this._rand() - 0.5) * 0.9, a.rot[2] + (this._rand() - 0.5) * 0.4]
    };
  }

  _blend(A, B, e) {
    const m = (x, y) => x + (y - x) * e, mv = (a, b) => a.map((x, i) => m(x, b[i]));
    return {
      idx: B.idx, name: B.name, s1: mv(A.s1, B.s1), s2: mv(A.s2, B.s2), ku: m(A.ku, B.ku), kv: m(A.kv, B.kv), roll: m(A.roll, B.roll), twist: m(A.twist, B.twist),
      bulb: m(A.bulb, B.bulb), waist: m(A.waist, B.waist), trail: m(A.trail, B.trail), aspect: m(A.aspect, B.aspect), rot: mv(A.rot, B.rot)
    };
  }

  _launchWave(strength) {
    const ang = this._rand() * Math.PI * 2;
    this._waves.push({ t0: this._t, dx: Math.cos(ang), dy: Math.sin(ang) * 0.6, amp: 0.65 + 0.35 * clamp01(strength) });
    if (this._waves.length > 2) this._waves.shift();
    this._lastWave = this._t; this._waveCount++;
  }

  _launchSplit() {
    const ang = this._rand() * Math.PI;
    this._split = { t0: this._t, dx: Math.cos(ang), dy: Math.sin(ang) };
    this._lastSplit = this._t; this._splitCount++;
  }

  update(deltaTime, profile) {
    if (profile.playbackState !== 'PLAYING') return;      // pause : gel
    const dt = Math.min(deltaTime, 0.1);
    const a = this._adapt.update(dt, profile);
    this._clock.update(dt);
    const nerve = clamp01(a.intensity);
    this._t += dt * (0.8 + 0.45 * nerve);
    const t = this._t;

    // LES FORMES : une nouvelle cible toutes les 6 à 10 s, un fondu lent
    if (t >= this._nextShapeAt) {
      const e = smoother((t - this._shapeT0) / this._shapeDur);
      this._from = this._blend(this._from, this._to, e);
      this._to = this._makeShape(this._to.idx);
      this._shapeT0 = t; this._shapeDur = 4 + 2 * (1 - nerve);
      this._nextShapeAt = t + SHAPE_S[1] - (SHAPE_S[1] - SHAPE_S[0]) * nerve;
      this._metamorphoses++;
    }
    const sh = this._blend(this._from, this._to, smoother((t - this._shapeT0) / this._shapeDur));

    // LES VAGUES : sur les accents (et une vague naturelle au plus tard toutes les 12 s)
    const accent = a.attack > 0.55 && t - this._lastWave > WAVE_GAP_S;
    if (accent || (this._dbgWave && t - this._lastWave > 3) || t - this._lastWave > WAVE_FALLBACK_S) this._launchWave(accent ? a.attack : 0.6);
    if (!this._split && ((a.section && t - this._lastSplit > SPLIT_COOLDOWN_S) || (this._dbgSplit && t - this._lastSplit > 15))) this._launchSplit();

    const director = profile.director;
    const remain = director && director.sceneRemaining != null ? director.sceneRemaining : null;
    let dive = 0;
    if (this._dbgDive) { const c = (t % 10) / 10; dive = c > 0.6 ? clamp01((c - 0.6) / 0.3) : 0; }
    else if (remain != null && remain < DIVE_S) dive = clamp01(1 - remain / DIVE_S);
    this._dive = dive;

    // la trajectoire : dans les deux tiers supérieurs, autour du centre de composition
    const asp = safeZoneUniforms.uAspect.value || 1.78;
    const X = Math.max(0.05, asp * 0.5 - HALF_W - 0.12);
    const cx = X * Math.sin(t * 0.071) * (0.75 + 0.25 * Math.cos(t * 0.023));
    const cy = 0.12 + 0.10 * Math.sin(t * 0.093 + 1.1) + 0.03 * Math.sin(t * 0.031);

    const U = this._birdU;
    U.uTime.value = t;
    U.uCenter.value.set(cx, cy);
    // 40 à 55 % de la largeur : la nappe à plat fait 2 HALF_W ; ses plis la raccourcissent
    U.uScale.value = HALF_W * Math.min(1, asp / 1.78) * (1 - 0.5 * dive);
    this._eu.set(sh.rot[0] + 0.20 * Math.sin(t * 0.11), sh.rot[1] + 0.35 * Math.sin(t * 0.07), sh.rot[2] + 0.15 * Math.sin(t * 0.13));
    this._rot.makeRotationFromEuler(this._eu);
    U.uRot.value.setFromMatrix4(this._rot);
    U.uS.value.set(sh.s1[0], sh.s1[1], sh.s2[0], sh.s2[1]);
    U.uK.value.set(sh.ku + 0.12 * Math.sin(t * 0.17), sh.kv + 0.15 * Math.sin(t * 0.13), sh.roll, sh.twist + 0.15 * Math.sin(t * 0.12));
    U.uB.value.set(sh.bulb * (0.8 + 0.2 * Math.sin(t * 0.3)), sh.waist, sh.trail, sh.aspect);
    for (let k = 0; k < 2; k++) {
      const w = this._waves[k], v = U.uWave.value[k];
      if (!w) { v.w = 0; continue; }
      const p = (t - w.t0) / WAVE_S;
      if (p > 1) { v.w = 0; continue; }
      v.set(w.dx, w.dy, -1.4 + 2.8 * p, w.amp * Math.sin(Math.PI * p));
    }
    this._waves = this._waves.filter((w) => (t - w.t0) / WAVE_S <= 1);
    if (this._split) {
      const p = (t - this._split.t0) / SPLIT_S;
      const amt = p < 0.35 ? smoother(p / 0.35) : p < 0.6 ? 1 : 1 - smoother((p - 0.6) / 0.4);
      U.uSplit.value.set(this._split.dx, this._split.dy, 0.30, amt);
      if (p >= 1) { this._split = null; U.uSplit.value.w = 0; }
    } else U.uSplit.value.w = 0;
    U.uDive.value.set(dive, Math.max(-X, Math.min(X, cx * 0.8)), HORIZON - 0.04);
    this._skyU.uTime.value = t;
    this._landU.uTime.value = t;
  }

  _beforeRender(renderer) {
    const tg = renderer.getRenderTarget();
    let h;
    if (tg) h = tg.height; else { const v = renderer.getDrawingBufferSize(this._bs || (this._bs = new THREE.Vector2())); h = v.y; }
    this._landU.uPx.value = 1 / Math.max(1, h);
  }

  get probe() {
    return {
      t: this._t, scene: this._clock.t, shape: this._to.name, metamorphoses: this._metamorphoses, waves: this._waveCount,
      splits: this._splitCount, dive: this._dive, activeWaves: this._waves.length, intensity: this._adapt.get().intensity, center: this._birdU.uCenter.value.toArray()
    };
  }

  dispose() {
    if (this._scene) for (const m of [this._sky, this._birds, this._land]) { this._scene.remove(m); m.geometry.dispose(); m.material.dispose(); }
    if (this._sky) this._sky.onBeforeRender = null;
    this._scene = null;
  }
}
